/**
 * This file is for utility functions that create or modify
 * StacSearch objects
 */

import { log } from '@methanesat/log';
import {
    AreaEmissionsProducts,
    DateRanges,
    Platforms,
    STACCollection,
    SortDirection,
    StacFilter,
    StacFormattedBbox,
    StacSearch,
    StacSearchField,
    StacSearchSorter,
    isSTACCollectionId,
    isStacFilter,
    isStacFilterArg
} from '../../types';
import { getMonthEnd, getMonthStart, getQuarterEnd, getQuarterStart } from '@methanesat/ui-components';
import { GROUP_MAIR_BY_YEAR, SHOW_L3_OPTION } from '../../environmentVariables';
import { getCollectionNameArray } from './stacNameTransformations';

/**
 *
 * *____NOTE:____*
 *
 * Documentation for STAC Search features & options, both GET and POST requests:
 * https://github.com/stac-api-extensions/filter?tab=readme-ov-file
 * Search objects here & elsewhere in the app are structured as they would be
 * for POST requests. All requests are now GET, to make it possible to cache requests
 * and their results. Functions in this file - format<option>ForGET reformat the POST
 * request parameters to the format necessary for GET requests.
 *
 */

/**
 * Default Search parameters
 */
export const BASIC_SEARCH: StacSearch = {
    limit: 1000,
    collections: [
        ...(SHOW_L3_OPTION ? [STACCollection.MethaneAIR_Level3_Portal] : []),
        STACCollection.MethaneAIR_Level4_point_pruned,
        STACCollection.MethaneAIR_Level4_Portal,
        ...(SHOW_L3_OPTION ? [STACCollection.MethaneSAT_Level3_Portal] : []),
        STACCollection.MethaneSAT_Level4_Portal,
        STACCollection.MethaneSAT_Level4_Portal_twostep,
        STACCollection.MethaneSAT_Level4_point_pruned
    ],
    fields: {
        include: ['properties', 'collection', 'links', 'geometry', 'assets'],
        exclude: [
            'properties.proj:epsg',
            'properties.proj:wkt2',
            'properties.proj:transform',
            'properties.proj:bbox',
            'properties.proj:shape',
            'links'
        ]
    },
    sortby: [
        {
            field: 'properties.end_datetime',
            direction: SortDirection.Decending
        }
    ]
};

export const getBasicSearch = (): StacSearch => {
    return structuredClone(BASIC_SEARCH);
};

/**
 * Type guard for checking if a value is a STAC filter.
 */
export const isSTACFilter = (maybeFilter: unknown): maybeFilter is StacFilter =>
    maybeFilter !== null &&
    typeof maybeFilter === 'object' &&
    typeof (maybeFilter as StacFilter).op === 'string' &&
    Array.isArray((maybeFilter as StacFilter).args);

/**
 * Type guard for checking if a value is STAC collections.
 */
export const isSTACCollections = (maybeCollections: unknown): maybeCollections is STACCollection[] =>
    Array.isArray(maybeCollections) && maybeCollections.every((collection) => isSTACCollectionId(collection));

/**
 * Type guard for checking if a value is STAC sortby.
 */
export const isSTACSortby = (maybeSortby: unknown): maybeSortby is StacSearchSorter[] =>
    maybeSortby !== null &&
    Array.isArray(maybeSortby) &&
    typeof (maybeSortby[0] as StacSearchSorter).field === 'string' &&
    typeof (maybeSortby[0] as StacSearchSorter).direction !== 'undefined';

/**
 * Type guard for checking if a value is STAC fields.
 */
export const isSTACFields = (maybeFields: unknown): maybeFields is StacSearchField =>
    maybeFields !== null &&
    typeof maybeFields === 'object' &&
    Array.isArray((maybeFields as StacSearchField).include) &&
    Array.isArray((maybeFields as StacSearchField).exclude);

/**
 * Builds a STAC search filter
 * Note: the logic here is focused on filtering for a property=val queries (e.g. when searching for
 * target id).  It won't handle more complex queries.
 *
 * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/getItemSearch
 * See also https://github.com/stac-api-extensions/filter for GET and POST versions of many filter operators
 *
 * @example
 * formatFilterForGET({ op: '=', args: [{ property: 'a' }, b] });
 * // -> `a='b'`
 *
 * @example
 * formatFilter
 */
export const formatFilterForGET = (filter: StacFilter): string => {
    const { op: operator, args } = filter;

    if (args.every(isStacFilter)) {
        const result = args.map(formatFilterForGET).join(` ${operator} `);
        return result;
    }

    if (isStacFilterArg(args)) {
        const [filterProperty, filterValue] = args;

        const val = formatValForGET(filterValue);

        const result = `${filterProperty.property}${operator}${val}`;
        return result;
    }
    throw Error(`malformed STAC filter ${JSON.stringify(filter)}`);
};

const formatValForGET = (val: unknown): string => {
    /**
     * Needs to be quoted or else CQL2 mistakenly thinks the dash `-` in `mair-target1` is a
     * logical operator.
     *
     * Single quotes don't need to be escaped, they're unreserved characters in URIs, see
     * `2.3. Unreserved Characters` in https://www.ietf.org/rfc/rfc2396.txt
     */
    if (typeof val === 'string') {
        return `'${val}'`;
    }
    if (typeof val === 'number') {
        return `${val}`;
    }
    if (Array.isArray(val)) {
        return `( ${val.map(formatValForGET).join(', ')} )`;
    }
    return `'${val}'`;
};

/**
 * Builds a STAC search sortby.
 * Note: GET output currently only supports sorting by one field (STAC search itself supports
 * sorting by multiple fields).
 *
 * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/getItemSearch
 *
 * @example
 * formatSortbyForGET([ { field: 'properties.end_datetime', direction: SortDirection.Ascending } ]);
 * // -> '+properties.end_datetime'
 */
export const formatSortbyForGET = (stacSearchSorter: StacSearchSorter[]) => {
    const { field, direction } = stacSearchSorter[0];

    // `-` is descending, `+` is ascending
    return `${direction === SortDirection.Decending ? '-' : '+'}${field}`;
};

/**
 * Formats collections for a STAC search GET request.
 *
 * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/getItemSearch
 *
 * @example
 * formatCollectionsForGET([STACCollection.MethaneSAT_Level4_Portal, STACCollection.MethaneSAT_Level4_point_pruned]);
 * // -> 'MethaneSAT_Level4_Portal,MethaneSAT_Level4_point_pruned'
 */
export const formatCollectionsForGET = (collections: STACCollection[]) => collections.join(',');

const addExcludePrefix = (STACField: string) => `-${STACField}`;

/**
 * Formats STAC search fields for a GET request.
 * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/getItemSearch
 *
 * @example
 * formatFieldsForGET({ include: ['a', 'b'], exclude: ['c', 'd'] });
 * // -> 'a,b,-c,-d'
 */
export const formatFieldsForGET = (fields: StacSearchField) => {
    const { include: rawInclude, exclude: rawExclude } = fields;

    const include = Array.isArray(rawInclude) ? rawInclude : [];

    // Exclude fields are prefixed with `-`.
    const exclude = Array.isArray(rawExclude) ? rawExclude.map(addExcludePrefix) : [];

    return [...include, ...exclude].join(',');
};

export const formatBbox = (bbox: number[][]): StacFormattedBbox => {
    const corner1 = bbox[0];
    const corner2 = bbox[2];

    const maxLng = Math.max(corner1[0], corner2[0]);
    const maxLat = Math.max(corner1[1], corner2[1]);
    const minLng = Math.min(corner1[0], corner2[0]);
    const minLat = Math.min(corner1[1], corner2[1]);

    return [minLng, minLat, maxLng, maxLat];
};

/**
 *
 * @param date the date on which to base the search. The returned stac search will request the entire calendar month or quarter that includes the given date.
 * @param search the STAC search object to be modified
 * @param dateRange the length of time - ex. `DateRanges.Month`
 * @returns STAC search object, which can be passed to the `/stac/search` endpoint as the request body
 */
export const getTimeBoundStacSearch = (date: Date, search: StacSearch, dateRange: DateRanges) => {
    const clonedSearch = structuredClone(search);

    if (dateRange === DateRanges.Month) {
        clonedSearch.datetime = getDatetimeOneMonthRangeString(date);
    } else if (dateRange === DateRanges.Quarter) {
        clonedSearch.datetime = getDatetimeQuarterlyRangeString(date);
    } else if (dateRange === DateRanges.Year) {
        clonedSearch.datetime = getDatetimeYearlyRangeString(date);
    } else {
        log.error(`Cannot search using the timeframe ${dateRange}`);
    }
    return clonedSearch;
};

/**
 * Get a 24hr day range
 * @param endDate
 * @returns
 */
export const getSingleDayRangeString = (endDate: Date) => {
    const start = new Date(endDate.getTime());
    // set time to midnight
    start.setUTCHours(0, 0, 0, 0);
    const end = new Date(start.getTime());
    end.setUTCDate(endDate.getUTCDate() + 1);

    const startString = start.toISOString();
    const endString = end.toISOString();
    return `${startString}/${endString}`;
};

export const getMonthEndString = (inputDate: Date) => {
    const monthEnd = getMonthEnd(inputDate);
    return monthEnd.toISOString();
};

/**
 * Get a datetime range string for a single calendar month
 * For STAC searches
 * Returns a range of one month based on the UTC date of @date
 *
 * Example:
 * `getDatetimeOneMonthRangeString(new Date('Feb 14 2023'))`
 * returns string for Feb 1 - Feb 28 2023:
 * `"2023-02-01T00:00:00.000Z/2023-02-28T23:59:59.999Z"`
 */
export const getDatetimeOneMonthRangeString = (date: Date): string => {
    const startDate = getMonthStart(date);
    const startString = startDate.toISOString();

    const endDate = getMonthEnd(date);
    const endString = endDate.toISOString();

    return `${startString}/${endString}`;
};

export const getDatetimeQuarterlyRangeString = (date: Date): string => {
    const startDate = getQuarterStart(date);
    const startString = startDate.toISOString();

    const endDate = getQuarterEnd(date);
    const endString = endDate.toISOString();

    return `${startString}/${endString}`;
};

export const getDatetimeYearlyRangeString = (date: Date): string => {
    const startOfYear = new Date(date.getUTCFullYear(), 0, 1);
    const startString = startOfYear.toISOString();

    const endOfYear = new Date(date.getUTCFullYear(), 11, 31, 23, 59, 59, 999);
    const endString = endOfYear.toISOString();

    return `${startString}/${endString}`;
};

export const getDateRangeFromPlatform = (platform: Platforms) => {
    return platform === Platforms.MSAT ? DateRanges.Quarter : GROUP_MAIR_BY_YEAR ? DateRanges.Year : DateRanges.Month;
};

export const getDateRangeEnd = (date: number, dateRange: DateRanges) => {
    if (dateRange === DateRanges.Quarter) return getQuarterEnd(new Date(date));
    return getMonthEnd(new Date(date));
};

export const getDateRangeStart = (date: number, dateRange: DateRanges) => {
    if (dateRange === DateRanges.Quarter) return getQuarterStart(new Date(date));
    return getMonthStart(new Date(date));
};

/**
 * Modify search params to query around a given point
 * @param lng longitude
 * @param lat latitude
 * @param search other serach parameters
 * @returns
 */
export const modifySearchByPoint = (lng: number, lat: number, search?: StacSearch) => {
    if (!search) {
        search = getBasicSearch();
    }
    search = structuredClone(search);
    search.intersects = {
        type: 'Point',
        coordinates: [lng, lat]
    };
    return search;
};

/**
 * Modify search params to query around a given bounding box
 * @param bbox bounding box to intersect
 * @param search other search parameters
 * @returns
 */
export const modifySearchByBoundingBox = (bbox: number[][], search?: StacSearch) => {
    if (!search) {
        search = getBasicSearch();
    }
    search = structuredClone(search);
    search.bbox = formatBbox(bbox);
    return search;
};

/**
 * Modify the item the search will perform for
 * @param itemId the item id to search for
 */
export const modifySearchItem = (itemId: string, search?: StacSearch) => {
    if (!search) {
        search = getBasicSearch();
    }
    search = structuredClone(search);
    search.ids = [itemId];
    return search;
};

/**
 * Modify the collection the search will perform for
 * @param platform if you want Sat or Air data (will not provide both as basic search will do this anyway for l4)
 * @param product Data processing level (L3 is concentrations, L4 is emissions).
 */
export const modifySearchCollection = (platform: Platforms, product: AreaEmissionsProducts, search?: StacSearch) => {
    if (!search) {
        search = getBasicSearch();
    }
    search = structuredClone(search);
    search.collections = getCollectionNameArray(platform, product);
    return search;
};
